import { forwardRef, useMemo, type ReactNode, type Ref } from 'react'
import { useWindowDimensions } from 'react-native'
import CarouselBase, { type CarouselRenderItem, type ICarouselInstance } from 'react-native-reanimated-carousel'

import { Box } from '@/components'
import { useIsMobileScreen } from '@/hooks'

export interface CarouselProps<T> {
  data: T[]
  renderItem: CarouselRenderItem<T>
  width?: number
  height?: number
  containerStyle?: any
  style?: any
  onSnapToItem?: (index: number) => void
  autoPlay?: boolean
  autoPlayInterval?: number
  loop?: boolean
  children?: ReactNode
  ref?: Ref<ICarouselInstance>
}

export type { ICarouselInstance, CarouselRenderItem }

/**
 * Web Carousel Component
 */
export const Carousel = forwardRef<ICarouselInstance, CarouselProps<any>>(
  ({ data, renderItem, onSnapToItem, autoPlay, autoPlayInterval, loop, children }, ref) => {
    const dimensions = useWindowDimensions()
    const isMobile = useIsMobileScreen()
    const carouselSize = useMemo(
      () => ({
        width: isMobile ? dimensions.width : 400,
        height: isMobile ? dimensions.width : 400
      }),
      [dimensions.width, isMobile]
    )

    return (
      <Box className='w-full items-center justify-center'>
        <CarouselBase
          ref={ref}
          data={data}
          renderItem={renderItem}
          width={carouselSize.width}
          height={carouselSize.height}
          containerStyle={{
            width: '100%',
            maxWidth: carouselSize.width,
            alignSelf: 'center' as const
          }}
          style={{
            width: '100%',
            maxWidth: carouselSize.width,
            alignSelf: 'center' as const
          }}
          onSnapToItem={onSnapToItem}
          autoPlay={autoPlay}
          autoPlayInterval={autoPlayInterval}
          loop={loop}
        >
          {children}
        </CarouselBase>
      </Box>
    )
  }
)

Carousel.displayName = 'Carousel'

export default Carousel
